import { FC, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { UpcomingMatch } from "../../models/UpcomingMatch";
import { divisionMappings } from "../../Constants";
import { parseMatchTime } from "../../utils/matchTime";
import styles from '../../styles/Tables.module.css';
import buttonStyles from '../../styles/MatchButton.module.css';

interface UpcomingMatchListProps {
    matches: UpcomingMatch[];
    /** Season carried into team links, so they open the right season's team page. */
    season?: number;
    /** Badge the division - worth it only when the team is playing across more than one. */
    showDivision?: boolean;
}

const getDivisionBadge = (division: string) => {
    if (division === 'ligaen') return 'L';
    const match = division.match(/^(\d)div([A-Z])?$/);
    if (match) return `${match[1]}${match[2] ?? 'D'}`;
    return division.substring(0, 3).toUpperCase();
};

const logoSrc = (logoUrl: string | undefined, width: number) => {
    if (!logoUrl) return '';
    return logoUrl.includes('getTeamLogos.php') && !logoUrl.includes('width=')
        ? `${logoUrl}&width=${width}`
        : logoUrl;
};

/**
 * The player tab's fixture list: one row per match, same column shape as the played-matches
 * table next to it. The shared UpcomingMatchTable groups into a table per day, which on a
 * profile means a dozen repeated headers for a dozen single matches.
 */
const UpcomingMatchList: FC<UpcomingMatchListProps> = ({ matches, season, showDivision }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Soonest first: the next match is what the player came here for.
    const sorted = useMemo(
        () => [...matches].sort((a, b) => parseMatchTime(a.match_time).getTime() - parseMatchTime(b.match_time).getTime()),
        [matches]
    );

    const buildTeamLink = (teamId: string) => season !== undefined ? `/team/${teamId}?season=${season}` : `/team/${teamId}`;

    const teamCell = (team: UpcomingMatch['team1']) => (
        <td className="align-middle text-center" style={{ padding: isMobile ? '0.5rem' : undefined }}>
            <Link to={buildTeamLink(team.teamId)} className="text-decoration-none text-light d-flex flex-column align-items-center">
                <img
                    src={logoSrc(team.teamLogo, isMobile ? 60 : 80)}
                    alt={team.teamName}
                    style={{ width: isMobile ? 30 : 40, height: isMobile ? 30 : 40, objectFit: 'contain' }}
                    loading="lazy"
                />
                <span className="mt-1 text-truncate" style={{ maxWidth: '100%' }}>{team.teamName}</span>
            </Link>
        </td>
    );

    return (
        <div className={`${styles.tableContainer} ${styles.dark}`}>
            <table className={`${styles.table} ${isMobile ? styles.compact : ''}`} style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                    <tr className={`${styles.tableHeader} ${styles.dark}`}>
                        <th className={`${styles.tableHeaderCell} ${styles.dark}`} style={{ width: isMobile ? '25%' : '22%' }}>Dato</th>
                        <th className={`${styles.tableHeaderCell} ${styles.dark}`} style={{ width: isMobile ? '30%' : '28%' }}>Hold 1</th>
                        <th className={`${styles.tableHeaderCell} ${styles.center} ${styles.dark}`} style={{ width: isMobile ? '15%' : '22%' }}>VS</th>
                        <th className={`${styles.tableHeaderCell} ${styles.dark}`} style={{ width: isMobile ? '30%' : '28%' }}>Hold 2</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map(match => {
                        const time = parseMatchTime(match.match_time);
                        return (
                            <tr
                                key={match.challonge_id}
                                className={`${styles.tableRow} ${styles.standard} ${styles.dark} ${isMobile ? styles.mobile : ''}`}
                            >
                                <td className="align-middle text-center">
                                    <div className="d-flex flex-column align-items-center">
                                        {showDivision && match.division && (
                                            <span
                                                className="badge mb-1"
                                                style={{
                                                    backgroundColor: match.division === 'ligaen' ? '#f15c25' : '#6c757d',
                                                    fontSize: '0.7rem',
                                                    padding: '2px 6px'
                                                }}
                                                title={divisionMappings.get(match.division) || match.division}
                                            >
                                                {getDivisionBadge(match.division)}
                                            </span>
                                        )}
                                        <span>
                                            {time.toLocaleDateString('da-DK', isMobile
                                                ? { day: 'numeric', month: 'short' }
                                                : { weekday: 'short', day: 'numeric', month: 'long' })}
                                        </span>
                                        <span style={{ color: '#ff9933', fontWeight: 600 }}>
                                            {time.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                        </span>
                                    </div>
                                </td>
                                {teamCell(match.team1)}
                                <td className="align-middle text-center" style={{ padding: isMobile ? '0.5rem' : undefined }}>
                                    <Link
                                        to={`/prematch/${match.challonge_id}`}
                                        className={`${buttonStyles.matchButton} ${isMobile ? buttonStyles.small : ''}`}
                                    >
                                        Se kamp
                                    </Link>
                                </td>
                                {teamCell(match.team2)}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default UpcomingMatchList;
